import { useState } from "react";
import { CheckCircle2, XCircle, RotateCcw, Send } from "lucide-react";
import { useUser } from "../context/UserContext";
import { Button } from "./ui/button";

export default function QuizBlock({ moduleId, topic }) {
  const { submitQuiz } = useUser();
  const quizzes = topic.quizzes || [];
  const [answers, setAnswers] = useState(() => quizzes.map(() => -1));
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = answers.every((a) => a !== -1) && !submitting && !result;

  const choose = (qi, oi) => {
    if (result) return;
    setAnswers((prev) => {
      const next = [...prev];
      next[qi] = oi;
      return next;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await submitQuiz(moduleId, topic.id, answers);
      setResult(res);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setResult(null);
    setAnswers(quizzes.map(() => -1));
  };

  if (quizzes.length === 0) return null;

  return (
    <div
      data-testid={`quiz-block-${topic.id}`}
      className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="text-xs uppercase tracking-[0.22em] text-brand-amber font-semibold">
            Quick Check
          </span>
          <h4 className="font-display text-2xl mt-1">Test what you just learned</h4>
        </div>
        {result && (
          <div
            data-testid={`quiz-score-${topic.id}`}
            className="text-right"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Your score</div>
            <div className="font-display text-3xl">
              {result.correct}/{result.total}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-8">
        {quizzes.map((q, qi) => {
          const chosen = answers[qi];
          const reviewItem = result?.items?.[qi];
          return (
            <div key={qi} data-testid={`quiz-question-${topic.id}-${qi}`}>
              <p className="text-base sm:text-lg font-medium mb-3 leading-snug">
                <span className="text-brand-amber mr-2">Q{qi + 1}.</span>
                {q.q}
              </p>
              <div className="grid gap-2">
                {q.options.map((opt, oi) => {
                  const isChosen = chosen === oi;
                  const isCorrect = reviewItem && reviewItem.correct === oi;
                  const isWrongChoice = reviewItem && reviewItem.chosen === oi && !reviewItem.is_correct;

                  let stateClass =
                    "border-border bg-background hover:border-brand-amber/40";
                  if (!result && isChosen)
                    stateClass = "border-brand-amber bg-brand-amber/10";
                  if (isCorrect)
                    stateClass = "border-emerald-500/70 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400";
                  if (isWrongChoice)
                    stateClass = "border-red-500/70 bg-red-500/10 text-red-700 dark:text-red-400";

                  return (
                    <button
                      key={oi}
                      type="button"
                      onClick={() => choose(qi, oi)}
                      disabled={!!result}
                      data-testid={`quiz-option-${topic.id}-${qi}-${oi}`}
                      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 flex items-start gap-3 ${stateClass} ${
                        !result ? "active:scale-[0.99] cursor-pointer" : "cursor-default"
                      }`}
                    >
                      <span className="inline-flex items-center justify-center h-6 w-6 shrink-0 rounded-full bg-muted text-xs font-semibold mt-0.5">
                        {String.fromCharCode(65 + oi)}
                      </span>
                      <span className="text-sm leading-relaxed flex-1">{opt}</span>
                      {isCorrect && <CheckCircle2 className="h-5 w-5 shrink-0" />}
                      {isWrongChoice && <XCircle className="h-5 w-5 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {reviewItem && (
                <div
                  data-testid={`quiz-explanation-${topic.id}-${qi}`}
                  className={`mt-3 text-sm leading-relaxed rounded-xl p-4 border ${
                    reviewItem.is_correct
                      ? "bg-emerald-500/5 border-emerald-500/30"
                      : "bg-amber-500/5 border-amber-500/30"
                  }`}
                >
                  <span className="font-semibold mr-1">
                    {reviewItem.is_correct ? "Correct." : "Not quite."}
                  </span>
                  {reviewItem.explanation}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        {!result ? (
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid={`quiz-submit-${topic.id}`}
            className="rounded-full bg-brand-amber hover:bg-brand-amber-hover text-white dark:text-brand-ink"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Submitting…" : "Submit answers"}
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={handleRetry}
            data-testid={`quiz-retry-${topic.id}`}
            className="rounded-full"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Try again
          </Button>
        )}
        {!result && (
          <span className="text-xs text-muted-foreground">
            Pick an answer for every question to submit.
          </span>
        )}
      </div>
    </div>
  );
}
